import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import type { User } from 'firebase/auth';
import { db, functions } from '@/firebase';
import { profileService } from '@/services/ProfileService';
import { TestType } from '@/models/SharedTypes';
import type { DiagnosticExamType } from '@/utils/diagnosticMemory';
import { buildRetakeReport, EMPTY_RETAKE_ANSWERS } from '@/features/onboarding/retakeReport';
import { contextProfileFill } from '@/utils/learnerContext';
import { callWithTimeout } from '@/utils/callWithTimeout';
import { logger } from '@/utils/logger';
import type { AimAnswers } from '@/features/onboarding/AimScreen';

/** Onboarding's exam (LBSW/LMSW/LCSW) → the TestType enum the profile writes use. */
export const EXAM_TO_TEST_TYPE: Record<DiagnosticExamType, TestType> = {
  LBSW: TestType.LBSW,
  LMSW: TestType.LMSW,
  LCSW: TestType.LCSW,
};

/** Onboarding.tsx keeps studyGoal at this default; we persist the same value. */
export const DEFAULT_STUDY_GOAL = 'Pass my exam on the first try';

/** Null when unreadable: callers then treat every field as already answered. */
export async function readUserDoc(uid: string): Promise<Record<string, unknown> | null> {
  try {
    return (await getDoc(doc(db, 'users', uid))).data() ?? {};
  } catch {
    return null;
  }
}

/**
 * The aim write carries the fields the Dashboard onboarding check reads
 * (onboardingCompleted, name, studyGoal), and must land BEFORE the first
 * getTrialPlan: the backend builds and freezes day 1 from targetExam on that read.
 * Throws when the write fails.
 */
export async function saveAimProfile(
  aim: AimAnswers,
  user: Pick<User, 'uid' | 'displayName'> | null | undefined,
  profile: { name?: string; studyGoal?: string } | null | undefined
): Promise<void> {
  const aimTrack = EXAM_TO_TEST_TYPE[aim.exam];
  // The profile payload has no retakeReport and the server writes it whole, so
  // the toggle only adds one when the doc has none (unreadable counts as has one).
  const stored = user ? await readUserDoc(user.uid) : null;
  const retake =
    aim.retake && stored && !stored.retakeReport
      ? buildRetakeReport({ ...EMPTY_RETAKE_ANSWERS, hasRetaken: true }, aimTrack)
      : null;
  // Only what she answered, plus the Dashboard-check name/studyGoal when missing;
  // an existing exam date or study time is never touched.
  await profileService.updateProfile({
    name: profile?.name?.trim() ? undefined : user?.displayName?.trim() || undefined,
    studyGoal: profile?.studyGoal?.trim() ? undefined : aim.retake ? 'Pass this time' : DEFAULT_STUDY_GOAL,
    onboardingCompleted: true,
    targetExam: aimTrack,
    ...(aim.date
      ? { examScheduleForTestType: { testType: aimTrack, entry: { status: 'scheduled', date: aim.date, updatedAt: '' } } }
      : { examDateStatus: 'none' as const }),
    retakeReport: retake ?? undefined,
    ...(stored ? contextProfileFill(stored.profile as Record<string, unknown> | undefined) : {}),
  });
}

/** Non-fatal: builds day 1 server-side so the first launch() doesn't race a cold plan. */
export async function warmTrialPlan(): Promise<void> {
  try {
    await callWithTimeout(() => httpsCallable(functions, 'getTrialPlan')({}), { timeoutMs: 8000, retries: 0 });
  } catch (err) {
    logger.warn('aimProfile', 'getTrialPlan warm-up failed (non-fatal)', { err });
  }
}
